import { useStore } from 'context/store-context';
import { Modal } from 'components/Modal';
import { Text } from 'components/Text';
import classNames from 'classnames';
import { ChevronRightIcon, XIcon } from '@heroicons/react/outline';
import { useEffect } from 'react';
import { isShipaidAdmin } from 'utils';
import { useUser } from 'context/user-context';
import { Button } from './Button';

export const SelectStoreModal = ({
  isOpen,
  onClose,
  addNewStores,
}: {
  isOpen: null | boolean;
  onClose: () => void;
  addNewStores?: boolean;
}) => {
  const {
    storeId,
    storeDomain,
    userStores,
    updateUserStore,
    storeProperties,
    clearStoreSelected,
  } = useStore();
  const { user } = useUser();

  useEffect(() => {
    const addingNewStoreProgressFlag = localStorage.getItem(
      'new-store-adding-progress',
    );
    if (
      (!storeId || !storeDomain) &&
      Array.isArray(userStores) &&
      userStores.length === 1 &&
      !addingNewStoreProgressFlag &&
      !Boolean(isOpen)
    ) {
      updateUserStore && updateUserStore(userStores[0]);
      return onClose();
    }
  }, [userStores, updateUserStore, onClose, storeDomain, storeId, isOpen]);

  const hasUninstalledApp =
    storeProperties?.uninstalledAt && storeDomain && storeId;

  // We have to distinguish between when isOpen is 'null' which is the default and when it
  // is 'false' which is when a user explicitly closes the modal.
  // if it is falsy and null i.e untouched, then let the store presence logic below take over
  // if it is falsy and 'false' i.e touched, then close the modal by shortcircuiting 'false' in the ternary below.
  const isOpenIsFalseyAndTouched = !isOpen && isOpen !== null;
  return (
    <Modal
      openModal={() => {}}
      open={
        !Boolean(isShipaidAdmin(user)) &&
        (isOpenIsFalseyAndTouched ? false : Boolean(isOpen))
      }
      containerClass="sm:max-w-lg sm:w-full shadow-none z-60"
      overlayClassName="bg-opacity-100 bg-white"
      isFull
    >
      <XIcon
        onClick={() => {
          onClose();
        }}
        className="w-5 h-5 absolute right-0 cursor-pointer"
        aria-hidden="true"
      />
      {Array.isArray(userStores) && userStores.length > 0 && (
        <Text value="Select A Store" type="h4" />
      )}
      {Array.isArray(userStores) && !hasUninstalledApp && (
        <ul className="my-8">
          {userStores?.map((store) => (
            <li
              key={store.domain}
              onClick={() => {
                updateUserStore && updateUserStore(store);
                onClose();
              }}
              className={`col-span-1 flex shadow-sm rounded-md mb-4 cursor-pointer`}
            >
              <div
                className={classNames(
                  `${store.storeColor}`,
                  'flex-shrink-0 flex items-center justify-center w-16 text-white text-sm font-medium rounded-l-md',
                )}
              >
                {store.initials}
              </div>
              <div
                className={`flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate ${
                  !(storeId === store.storeId) ? '!bg-white' : '!bg-blue-200'
                }`}
              >
                <div className="flex-1 px-4 py-2 text-sm truncate">
                  <Text
                    value={store.name || store.domain}
                    className="text-gray-900 font-medium hover:text-gray-600"
                  />
                  <Text className="text-gray-500" value={store.domain} />
                </div>
                <div className="flex-shrink-0 pr-2">
                  <button
                    type="button"
                    className="w-8 h-8 bg-white inline-flex items-center justify-center text-gray-400 rounded-full bg-transparent hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="sr-only">Open options</span>
                    <ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {addNewStores && (
        <Button
          onClick={() => {
            localStorage.setItem('new-store-adding-progress', 'true');
            clearStoreSelected?.();
            window.location.reload();
          }}
        >
          Add New Store
        </Button>
      )}
    </Modal>
  );
};
